import { mkdir, writeFile } from "node:fs/promises";
import { dirname, relative, sep } from "node:path";
import type { IslandModule } from "./scan.js";

// --- Client entry generator ---
//
// Turns a list of scanned islands into a client entry module that imports each
// island and registers it with `hydrateIslands`. This removes the need to hand-
// maintain `entry-client.ts` as islands are added or removed.
//
// The generated file imports island default exports and passes them to
// `hydrateIslands` keyed by their registry name.

/** Options for generating the client entry module. */
export interface GenerateEntryOptions {
  /** Islands to register, from `scanIslands`. */
  islands: IslandModule[];
  /** Absolute path of the entry file to write (e.g. ".nix-js/entry-client.ts"). */
  outFile: string;
  /**
   * Import specifier for the kit's client island helpers.
   * Defaults to the published subpath `@deijose/nix-js-kit/island`.
   */
  hydrateImport?: string;
  /**
   * Import specifier for the kit's client router.
   * Defaults to the published subpath `@deijose/nix-js-kit/router`.
   */
  routerImport?: string;
}

/** Turns a registry name into a safe JS identifier for the import binding. */
function toIdentifier(name: string, index: number): string {
  const cleaned = name.replace(/[^a-zA-Z0-9_$]/g, "_");
  return /^[a-zA-Z_$]/.test(cleaned) ? `${cleaned}_${index}` : `_${cleaned}_${index}`;
}

/** Builds the source code of the client entry module. */
export function buildEntrySource(
  islands: IslandModule[],
  outFile: string,
  hydrateImport = "@deijose/nix-js-kit/island",
  routerImport = "@deijose/nix-js-kit/router",
): string {
  const bindings = islands.map((island, i) => ({
    ident: toIdentifier(island.name, i),
    name: island.name,
    // Relative import specifier from the entry file to the island module.
    spec: toImportSpecifier(outFile, island.filePath),
  }));

  // Lazy registry: each island is loaded on-demand via dynamic import().
  // This enables code-splitting — islands not on the current page (or not yet
  // triggered by their directive) stay out of the initial bundle.
  //
  // The registry maps island name → discriminated lazy loader `{ load }`.
  // hydrateIslands() awaits `entry.load()` before hydrating, so the first
  // paint only needs the small entry chunk + the islands on the page. The
  // discriminated form lets the hydrator tell eager components from lazy
  // loaders without executing a probe.
  const registryLines = bindings
    .map((b) => `  ${JSON.stringify(b.name)}: { load: () => import(${JSON.stringify(b.spec)}).then(m => m.default) },`)
    .join("\n");

  const islandHydration = registryLines
    ? `const registry = {
${registryLines}
};
hydrateIslands(registry);
document.addEventListener("nix-js:rendered", () => {
  cleanupHydratedIslands();
  hydrateIslands(registry);
});

// Vite HMR: when an island module (or the entry itself) updates, dispose the
// current islands and re-hydrate from the updated modules — the registry's
// dynamic import() resolves to the fresh modules, so no full page reload is
// needed (progressive enhancement, audit §10.2 / §12.2).
if (import.meta.hot) {
  import.meta.hot.accept((newModule) => {
    cleanupHydratedIslands();
    hydrateIslands(registry);
    if (newModule) {
      // Re-run the module so its side effects (router, listeners) apply.
    }
  });
}`
    : "";

  return `// AUTO-GENERATED by @deijose/nix-js-kit. Do not edit.
import { startClientRouter } from ${JSON.stringify(routerImport)};
import { hydrateIslands, cleanupHydratedIslands } from ${JSON.stringify(hydrateImport)};

startClientRouter();
${islandHydration}
`;
}

/** Computes a POSIX-style relative import specifier between two files. */
function toImportSpecifier(fromFile: string, toFile: string): string {
  let spec = relative(dirname(fromFile), toFile).split(sep).join("/");
  if (!spec.startsWith(".")) spec = `./${spec}`;
  return spec;
}

/**
 * Generates and writes the client entry module for the given islands.
 *
 * @param options Generation options.
 * @returns The absolute path of the written entry file.
 */
export async function generateClientEntry(
  options: GenerateEntryOptions,
): Promise<string> {
  const source = buildEntrySource(
    options.islands,
    options.outFile,
    options.hydrateImport,
    options.routerImport,
  );
  await mkdir(dirname(options.outFile), { recursive: true });
  await writeFile(options.outFile, source, "utf8");
  return options.outFile;
}
